import type {
  AgentBillingSubscriptionInterval,
  FeatureType,
  Price,
  Product,
  RateCard,
  SubscriptionItem,
  SubscriptionView,
} from "schemas/agent-billing";

import { formatNumber } from "replo-utils/lib/format";
import { pluralize } from "replo-utils/lib/pluralize";
import { PLAN_PRODUCT_TYPES } from "schemas/agent-billing";
import { ReploValidationError } from "schemas/errors";

import { price } from "./price";

function findAddOnProduct({
  rateCard,
  productType,
}: {
  rateCard: RateCard;
  productType: string;
}): Product | undefined {
  return rateCard.addOnProducts.find(
    (product) => product.productType === productType,
  );
}

function findPrice({
  rateCard,
  priceId,
}: {
  rateCard: RateCard;
  priceId: string;
}): Price | undefined {
  const allPrices = [
    ...rateCard.prices,
    ...rateCard.addOnProducts.flatMap((product) => product.prices),
  ];
  return allPrices.find((priceEntry) => priceEntry.priceId === priceId);
}

function getDefaultProductPrice({
  rateCard,
  productType,
  interval,
}: {
  rateCard: RateCard;
  productType: string;
  interval: AgentBillingSubscriptionInterval;
}): Price | undefined {
  if (PLAN_PRODUCT_TYPES.has(productType)) {
    return price.findDefaultByInterval({ prices: rateCard.prices, interval });
  }
  const addOnProduct = findAddOnProduct({ rateCard, productType });
  if (!addOnProduct) {
    return undefined;
  }
  return price.findDefaultByInterval({
    prices: addOnProduct.prices,
    interval,
  });
}

function getPlanSubscriptionItem(
  subscriptionView: SubscriptionView,
): SubscriptionItem {
  const planSubscriptionItem = subscriptionView.subscriptionItems.find(
    (subscriptionItem) => {
      return PLAN_PRODUCT_TYPES.has(subscriptionItem.productType);
    },
  );
  if (!planSubscriptionItem) {
    throw new ReploValidationError("Subscription must have a plan item");
  }
  return planSubscriptionItem;
}

function getDefaultPriceId({
  rateCard,
  productType,
  interval,
}: {
  rateCard: RateCard;
  productType: string;
  interval: AgentBillingSubscriptionInterval;
}): string | null {
  return (
    getDefaultProductPrice({ rateCard, productType, interval })?.priceId ?? null
  );
}

export const rateCard = {
  getDefaultProductPrice,
  getDefaultPriceId,

  getPercentSaved({ rateCard }: { rateCard: RateCard }): number {
    const monthly = price.findDefaultByInterval({
      prices: rateCard.prices,
      interval: "month",
    });
    const annual = price.findDefaultByInterval({
      prices: rateCard.prices,
      interval: "year",
    });
    if (!monthly || !annual || monthly.amount === 0) {
      return 0;
    }
    return Math.round(
      (1 - price.getPerMonth(annual).amount / monthly.amount) * 100,
    );
  },

  getSubscriptionMonthlyTotal({
    rateCard,
    subscriptionView,
  }: {
    rateCard: RateCard;
    subscriptionView: SubscriptionView;
  }): { amount: number; currency: string } | null {
    const planSubscriptionItem = getPlanSubscriptionItem(subscriptionView);
    const basePrice =
      findPrice({
        rateCard,
        priceId: planSubscriptionItem.priceId,
      }) ??
      getDefaultProductPrice({
        rateCard,
        productType: planSubscriptionItem.productType,
        interval: planSubscriptionItem.interval,
      });
    if (!basePrice) {
      return null;
    }
    const basePricePerMonth = price.getPerMonth(basePrice);

    let addOnTotal = 0;
    for (const subscriptionItem of subscriptionView.subscriptionItems) {
      if (PLAN_PRODUCT_TYPES.has(subscriptionItem.productType)) {
        continue;
      }
      const subscriptionItemPrice =
        findPrice({
          rateCard,
          priceId: subscriptionItem.priceId,
        }) ??
        getDefaultProductPrice({
          rateCard,
          productType: subscriptionItem.productType,
          interval: planSubscriptionItem.interval,
        });
      if (!subscriptionItemPrice) {
        continue;
      }
      addOnTotal +=
        price.getPerMonth(subscriptionItemPrice).amount *
        subscriptionItem.quantity;
    }

    return {
      amount: basePricePerMonth.amount + addOnTotal,
      currency: basePricePerMonth.currency,
    };
  },

  getSubscriptionYearTotal({
    rateCard,
    subscriptionView,
  }: {
    rateCard: RateCard;
    subscriptionView: SubscriptionView;
  }): { amount: number; currency: string } | null {
    const planSubscriptionItem = getPlanSubscriptionItem(subscriptionView);
    const annualPrice =
      findPrice({
        rateCard,
        priceId: planSubscriptionItem.priceId,
      }) ??
      getDefaultProductPrice({
        rateCard,
        productType: planSubscriptionItem.productType,
        interval: "year",
      });
    if (!annualPrice) {
      return null;
    }

    let addOnTotal = 0;
    for (const subscriptionItem of subscriptionView.subscriptionItems) {
      if (PLAN_PRODUCT_TYPES.has(subscriptionItem.productType)) {
        continue;
      }
      const resolvedPrice =
        findPrice({
          rateCard,
          priceId: subscriptionItem.priceId,
        }) ??
        getDefaultProductPrice({
          rateCard,
          productType: subscriptionItem.productType,
          interval: "year",
        });
      if (!resolvedPrice) {
        continue;
      }
      if (resolvedPrice.interval === "year") {
        addOnTotal += resolvedPrice.amount * subscriptionItem.quantity;
      } else {
        addOnTotal += resolvedPrice.amount * 12 * subscriptionItem.quantity;
      }
    }

    return {
      amount: annualPrice.amount + addOnTotal,
      currency: annualPrice.currency,
    };
  },

  getUnitsForFeature({
    rateCard,
    subscriptionView,
    featureType,
  }: {
    rateCard: RateCard;
    subscriptionView: SubscriptionView;
    featureType: FeatureType;
  }): number | null {
    const included = rateCard.features.find(
      (feature) => feature.featureType === featureType,
    );
    if (included?.units === null) {
      return null;
    }

    const baseUnits = included?.units ?? 0;

    let addOnUnits = 0;
    for (const subscriptionItem of subscriptionView.subscriptionItems) {
      const addOnProduct = findAddOnProduct({
        rateCard,
        productType: subscriptionItem.productType,
      });
      if (!addOnProduct || addOnProduct.featureType !== featureType) {
        continue;
      }
      if (addOnProduct.units === null) {
        continue;
      }
      addOnUnits += addOnProduct.units * subscriptionItem.quantity;
    }

    return baseUnits + addOnUnits;
  },

  getLineItems({
    rateCard,
    subscriptionView,
  }: {
    rateCard: RateCard;
    subscriptionView: SubscriptionView;
  }) {
    const planSubscriptionItem = getPlanSubscriptionItem(subscriptionView);
    const lineItems: { label: string; amount: number; currency: string }[] = [];

    for (const subscriptionItem of subscriptionView.subscriptionItems) {
      if (
        subscriptionItem.quantity <= 0 ||
        PLAN_PRODUCT_TYPES.has(subscriptionItem.productType)
      ) {
        continue;
      }

      const addOnProduct = findAddOnProduct({
        rateCard,
        productType: subscriptionItem.productType,
      });
      if (!addOnProduct) {
        continue;
      }

      const addOnPrice =
        findPrice({
          rateCard,
          priceId: subscriptionItem.priceId,
        }) ??
        getDefaultProductPrice({
          rateCard,
          productType: subscriptionItem.productType,
          interval: planSubscriptionItem.interval,
        });
      if (!addOnPrice) {
        continue;
      }

      const monthlyPrice = price.getPerMonth(addOnPrice);
      const totalUnits = subscriptionItem.quantity * (addOnProduct.units ?? 1);

      lineItems.push({
        label: `${formatNumber(totalUnits)} Additional ${pluralize(addOnProduct.displayName, totalUnits)}`,
        amount: subscriptionItem.quantity * monthlyPrice.amount,
        currency: monthlyPrice.currency,
      });
    }

    return lineItems;
  },

  getFeatureCapacity({
    rateCard,
    featureType,
  }: {
    rateCard: RateCard;
    featureType: FeatureType;
  }): { current: number; maximum: number | null } {
    const feature = rateCard.features.find(
      (feature) => feature.featureType === featureType,
    );
    if (!feature) {
      return { current: 0, maximum: 0 };
    }
    if (feature.units === null) {
      return { current: feature.minimumCapacity, maximum: null };
    }
    let addOnUnits = 0;
    for (const product of rateCard.addOnProducts) {
      if (product.featureType === featureType) {
        addOnUnits += product.units * product.currentProductCount;
      }
    }
    return {
      current: feature.minimumCapacity,
      maximum: feature.units + addOnUnits,
    };
  },

  getProratedCharge({
    currentRateCard,
    currentSubscriptionView,
    nextRateCard,
    nextSubscriptionView,
    nextBillingDate,
  }: {
    currentRateCard: RateCard;
    currentSubscriptionView: SubscriptionView;
    nextRateCard: RateCard;
    nextSubscriptionView: SubscriptionView;
    nextBillingDate: Date;
  }): { amount: number; currency: string } | null {
    const currentMonthly = rateCard.getSubscriptionMonthlyTotal({
      rateCard: currentRateCard,
      subscriptionView: currentSubscriptionView,
    });
    const nextMonthly = rateCard.getSubscriptionMonthlyTotal({
      rateCard: nextRateCard,
      subscriptionView: nextSubscriptionView,
    });
    if (!currentMonthly || !nextMonthly) {
      return null;
    }
    const diff = nextMonthly.amount - currentMonthly.amount;

    const currentPlanSubscriptionItem = getPlanSubscriptionItem(
      currentSubscriptionView,
    );
    const currentInterval = currentPlanSubscriptionItem.interval;
    const now = new Date();
    const msRemaining = nextBillingDate.getTime() - now.getTime();

    const periodStart = new Date(nextBillingDate);
    if (currentInterval === "year") {
      periodStart.setFullYear(periodStart.getFullYear() - 1);
    } else {
      periodStart.setMonth(periodStart.getMonth() - 1);
    }

    const totalMs = nextBillingDate.getTime() - periodStart.getTime();
    const fullPeriodDiff = currentInterval === "year" ? diff * 12 : diff;

    return {
      amount: (fullPeriodDiff * msRemaining) / totalMs,
      currency: nextMonthly.currency,
    };
  },
};
